/**
 * Access Control (RBAC) API contract (WRK-5473). Base path /works-lite/access-control.
 * Authorisation is route-level only — `(action, subject)` pairs, no row-level conditions.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { actionSchema, subjectSchema } from './permissions';

const c = initContract();

// Shared response envelopes (mirrors the settings contract).
const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

const successResponseSchema = z.object({
  message: z.string(),
});

// Core schemas. `actionSchema`/`subjectSchema` derive from the permissions.ts source of truth.

// A permission entry's subject is the app set plus CASL's `'all'` wildcard (Admin's seeded
// blanket grant round-trips as `subject: 'all'`). Create/update still can't invent a subject.
export const permissionSubjectSchema = z.union([subjectSchema, z.literal('all')]);

// A single grant. No `conditions` field — Zod's default strip discards any stray key.
export const permissionEntrySchema = z.object({
  action: actionSchema,
  subject: permissionSubjectSchema,
});

// CASL packed rules: each is `[action, subject]`; the conditions/inverted/reason slots are unused.
export const packedRulesResponseSchema = z.object({
  rules: z.array(z.array(z.any())),
});

// Flat set of subjects the app authorises against; grouping/labels are left to the UI.
export const subjectsResponseSchema = z.object({
  subjects: z.array(subjectSchema),
});

export const roleSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  /** True when the role holds a blanket `subject: 'all'` grant (full access). */
  isAdmin: z.boolean(),
});

export const roleDetailSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(permissionEntrySchema),
});

export const rolesListResponseSchema = z.object({
  data: z.array(roleSummarySchema),
  total: z.number(),
});

export const createRoleBodySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  permissions: z.array(permissionEntrySchema),
});

export const updateRoleBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  permissions: z.array(permissionEntrySchema).optional(),
});

export const roleIdResponseSchema = z.object({
  id: z.number(),
});

export const assignUserRoleBodySchema = z.object({
  roleId: z.number(),
});

/** The role ids a user holds via `access_control_user_role`. */
export const userRolesResponseSchema = z.object({
  roleIds: z.array(z.number()),
});

export const accessControlContract = c.router({
  getPermissions: {
    method: 'GET',
    path: '/permissions',
    responses: {
      200: packedRulesResponseSchema,
      500: errorResponseSchema,
    },
    summary: "Get the current user's effective permissions as packed CASL rules",
  },

  listSubjects: {
    method: 'GET',
    path: '/subjects',
    responses: {
      200: subjectsResponseSchema,
      500: errorResponseSchema,
    },
    summary:
      'List the registered access-control subjects (flat list; grouping is a frontend concern)',
  },

  listRoles: {
    method: 'GET',
    path: '/roles',
    responses: {
      200: rolesListResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'List all access-control roles with permission counts',
  },

  getRole: {
    method: 'GET',
    path: '/roles/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    responses: {
      200: roleDetailSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a role and its (action, subject) permissions',
  },

  createRole: {
    method: 'POST',
    path: '/roles',
    body: createRoleBodySchema,
    responses: {
      201: roleIdResponseSchema,
      400: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new role',
  },

  updateRole: {
    method: 'PUT',
    path: '/roles/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: updateRoleBodySchema,
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update a role and/or replace its permission set',
  },

  deleteRole: {
    method: 'DELETE',
    path: '/roles/:id',
    pathParams: z.object({ id: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Delete a non-system role',
  },

  getUserRoles: {
    method: 'GET',
    path: '/users/:userId/roles',
    pathParams: z.object({ userId: z.coerce.number() }),
    responses: {
      200: userRolesResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get the ids of the roles assigned to a user via access_control_user_role',
  },

  assignUserRole: {
    method: 'POST',
    path: '/users/:userId/roles',
    pathParams: z.object({ userId: z.coerce.number() }),
    body: assignUserRoleBodySchema,
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Assign a role to a user',
  },

  removeUserRole: {
    method: 'DELETE',
    path: '/users/:userId/roles/:roleId',
    pathParams: z.object({ userId: z.coerce.number(), roleId: z.coerce.number() }),
    body: z.any().optional(),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Remove a role from a user',
  },
});

// Re-export the action/subject source of truth so the contract stays the single import surface.
export {
  Action,
  Subject,
  isAction,
  isSubject,
  isCapabilityEnforced,
  listActions,
  listSubjects,
  actionSchema,
  subjectSchema,
} from './permissions';

export type ActionValue = z.infer<typeof actionSchema>;
/** A matrix subject value (the closed app set, WITHOUT the `'all'` wildcard). */
export type SubjectValue = z.infer<typeof subjectSchema>;
export type PermissionEntry = z.infer<typeof permissionEntrySchema>;
export type PackedRulesResponse = z.infer<typeof packedRulesResponseSchema>;
export type SubjectsResponse = z.infer<typeof subjectsResponseSchema>;
export type RoleSummary = z.infer<typeof roleSummarySchema>;
export type RoleDetail = z.infer<typeof roleDetailSchema>;
export type RolesListResponse = z.infer<typeof rolesListResponseSchema>;
export type CreateRoleBody = z.infer<typeof createRoleBodySchema>;
export type UpdateRoleBody = z.infer<typeof updateRoleBodySchema>;
export type AssignUserRoleBody = z.infer<typeof assignUserRoleBodySchema>;
export type UserRolesResponse = z.infer<typeof userRolesResponseSchema>;
